import React, { useRef } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { getValuesBar, getBarLegend } from '@components/machines/components/eventsChart/calculation';
import { getBarDateRangeForPeriod } from '@components/machines/components/eventsChart/helpers/getBarDateRangeForPeriod';
import { UseManageBarChartParams } from '@components/machines/components/eventsChart/types';
import { computeFormattedData } from '@components/machines/components/eventsChart/helpers/computeFormattedData';
import { buildMachineStatsGraphParams } from '@reducers/statistics/helpers/buildStatisticsParams';
import { LOGGER_TITLES } from '@reducers/dataDogs/constants';

export const useManageBarChart = (params: UseManageBarChartParams) => {
  const {
    data,
    setData,
    selectedChartDate,
    isDay,
    selectedIndex,
    selectedItem,
    isActive,
    getStatsHour,
    getStatsDay,
    getStatsMonth,
    getStatsWeek,
    getStats,
    id,
    setNcFiles,
    sendLog,
    allSelected,
    selectedTimeCounter,
    chartWidth,
    chartHeight,
    showInitialLoader = false,
    isMachinesScope,
  } = params;

  const { timePeriod: selectedTime } = useHandleParams();
  const abortControllerRef = useRef<AbortController>();

  const start = data?.start;
  const { legend = [], legendRules = [] } = getBarLegend(data?.selectedTime, start);
  const dataBar = data?.dataBar;
  const loaded = !!data?.dataBar?.length;

  useFocusEffect(
    React.useCallback(() => {
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      (async () => {
        if ((id == null || id === '') && !isMachinesScope) return;
        if (abortController.signal.aborted) return;

        const dateRange = getBarDateRangeForPeriod(selectedTime, selectedChartDate);
        if (!dateRange) return;

        setNcFiles([]);
        const performanceStorage: Record<string, any> = {};

        try {
          const getRangeStatsByPeriod = () => {
            const statsMap = {
              hour: getStatsHour,
              day: getStatsDay,
              month: getStatsMonth,
              week: getStatsWeek,
            };
            return statsMap[dateRange.statsType];
          };

          if (abortController.signal.aborted) return;

          if (data?.selectedTime && data.selectedTime !== selectedTime) {
            setData({});
          }

          const t1 = performance.now();
          const apiData =
            isMachinesScope && getStats
              ? await getStats(buildMachineStatsGraphParams(selectedTime, dateRange.start, id, allSelected), abortController.signal)
              : await getRangeStatsByPeriod()?.({ id, start: dateRange.start, end: dateRange.end }, abortController.signal);
          const t2 = performance.now();
          performanceStorage.firstAPIcall = t2 - t1;
          if (!apiData) return;

          if (abortController.signal.aborted) return;

          const t3 = performance.now();
          const info = getValuesBar({ start: dateRange.start, data: apiData, amount: dateRange.amount, period: dateRange.period });
          const t4 = performance.now();
          performanceStorage.firstCalculation = t4 - t3;
          performanceStorage.firstAmount = apiData?.length;

          setData({
            ...info,
            selectedTime,
            start: dateRange.start,
            end: dateRange.end,
          });
        } catch (error: any) {
          if (error.name !== 'AbortError') {
            console.error('Error loading chart data:', error);
          }
        }

        sendLog({ title: LOGGER_TITLES.BAR_CHART_PERFORMANCE, data: { ...performanceStorage, selectedTime, machine: id }, status: 'debug' });
      })();

      return () => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
      };
    }, [selectedTime, id, selectedChartDate, selectedTimeCounter, isMachinesScope])
  );

  const isLoading = !loaded || showInitialLoader;

  const heightValues = isActive ? ['active'] : ['setup', 'change', 'active'];

  const barBlockWidth = chartWidth / (dataBar?.length || 1);
  const barWidth = barBlockWidth * 0.66;
  const legendLength = legend.length || 1;
  const bottomLegendBlockWidth = chartWidth / legendLength;

  const { formatedData, rightLegend } =
    !isLoading && dataBar ? computeFormattedData(dataBar, heightValues, isDay) : { formatedData: [], rightLegend: [0, 0, 0] };

  const rightFooter = selectedItem
    ? { active: selectedItem.active, setup: selectedItem.setup, change: selectedItem.change }
    : { active: data?.totalActive, setup: data?.totalSetup, change: data?.totalChange };

  return {
    isLoading,
    legend,
    legendRules,
    dataBar,
    formatedData,
    rightLegend,
    rightFooter,
    heightValues,
    barBlockWidth,
    barWidth,
    bottomLegendBlockWidth,
    chartHeight,
    chartWidth,
    selectedTime,
  };
};
